import Input from "components/Input";
import Button from "components/Button";
import clsx from "clsx";
import Modal from "components/Modal";
import { confirmMicroDepositBankAccount } from "services/api/haulpay";
import { showErrorMessage, showSuccessMessage } from "helpers/toastMessage";
import { useForm, Controller } from "react-hook-form";
import Icon from "@mdi/react";
import { mdiLoading } from "@mdi/js";
import { useMutation, useQueryClient } from "@tanstack/react-query";

interface IProps {
  open: boolean;
  closeHandler: () => void;
}

export default function ConfirmMicroDepositDepositModel({ open, closeHandler }: IProps) {
  const queryClient = useQueryClient();

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    reset
  } = useForm({
    defaultValues: {
      code: ""
    }
  });

  const codeWatcher = watch("code");

  // useMutation for handling the deposit action
  const mutation = useMutation((code: string) => confirmMicroDepositBankAccount(code), {
    onSuccess: () => {
      queryClient.invalidateQueries(["fetch_stripe_account"]);
      closeHandler();
      showSuccessMessage(
        "Micro Deposit Confirmation Successful. Please wait while we verify your deposit."
      );
      reset();
    },
    onError: () => {
      showErrorMessage("Failed to add bank account. Please try again later.");
    }
  });

  const submitDeposit = async (data: { code: string }) => {
    await mutation.mutateAsync(data.code);
  };
  return (
    <Modal
      isOpen={open}
      closeModal={closeHandler}
      wrapperClass="!my-auto desktop:!min-h-fit text-sm"
      dialogClass="!bg-foreground !p-4 !border !border-card !overflow-y-auto gap-2.5"
      closeClass="!top-24 !right-6 !bg-transparent !text-text">
      <span className="text-xl font-bold text-primary">Micro Deposit</span>
      <form onSubmit={handleSubmit(submitDeposit)}>
        <div className="mt-4 mb-2">
          <p className="text-sm">
            Enter the verification code from your bank statement, not the full transaction
            description. Look for a small deposit from <strong>Stripe</strong>. The description will
            look something like <strong>SMABCD-word</strong> (for example,{" "}
            <strong>SMYVXR-hurricane</strong>). Enter the SM followed by the 4-character code, and
            stop before the hyphen — in this example, <strong>SMYVXR</strong>. Do not include the
            hyphen or anything after it.
          </p>
          <div className="pt-4">
            <Controller
              name="code"
              control={control}
              rules={{
                required: "Code is required"
              }}
              render={({ field }) => (
                <Input
                  type="text"
                  placeholder="e.g. SMYVXR"
                  value={field.value}
                  onChange={field.onChange}
                  inputClass="!border !border-subtitle"
                />
              )}
            />
          </div>
          {errors.code && <span className="text-sm text-red-500">{errors.code.message}</span>}
        </div>
        <div className="flex gap-4 justify-between *:text-sm *:rounded-full *:h-9 pt-4 float-right">
          <Button
            className={clsx(
              !codeWatcher
                ? "!bg-subtitle !ring-subtitle cursor-not-allowed"
                : "bg-gradient-to-r from-primary to-secondary"
            )}
            disabled={!codeWatcher || isSubmitting}
            type="submit">
            {!codeWatcher || isSubmitting ? <Icon path={mdiLoading} size={1} spin={1} /> : "Submit"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
